
import LandingPage                        from './components/LandingPage';
import PrivacyPolicy                      from './components/PrivacyPolicy';
import TermsOfService                     from './components/TermsOfService';
import Signup                             from './components/auth/Signup';
import Login                              from './components/auth/Login';
import { ForgotPassword, ResetPassword }  from './components/auth/ForgotPassword';
import Dashboard                          from './components/Dashboard';
import Search                             from './components/Search';
import { supabase }                       from './lib/supabaseClient';
import Blog                               from './components/Blog';
import useIdleLogout from './lib/useIdleLogout';
import React, { useState, useEffect, Suspense, lazy } from 'react';

const BackOffice = lazy(() => import('./components/BackOffice'));

const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutes

const App = () => {
  const [page,               setPage]               = useState('home');
  const [session,            setSession]            = useState(null);
  const [checked,            setChecked]            = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [claimToken,         setClaimToken]         = useState(null);
  const [claiming,           setClaiming]           = useState(false);
  

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('type=recovery')) {
      setPage('reset-password');
      setChecked(true);
      return;
    }

    // Capture and clean URL params before session check
    const params  = new URLSearchParams(window.location.search);
    const vResult = params.get('verification');
    const cToken  = params.get('claim');
    if (vResult || cToken) {
      if (vResult) setVerificationResult(vResult);
      if (cToken)  setClaimToken(cToken);
      window.history.replaceState({}, '', window.location.pathname);
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session && !cToken) setPage('dashboard');
      setChecked(true);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (event === 'SIGNED_OUT') setPage('login');
    });

    return () => subscription.unsubscribe();
  }, []);

  // Execute profile claim when token is present and session check is done
  useEffect(() => {
    if (!claimToken || !checked) return;
    const executeClaim = async () => {
      setClaiming(true);
      try {
        const { data, error } = await supabase.functions.invoke('claim-profile', {
          body: { token: claimToken },
        });
        if (error || !data?.success) {
          const msg = data?.error || 'Profile claim failed. Please contact admin@umpire-iq.com.';
          alert(msg);
        }
      } catch {
        alert('Profile claim failed. Please contact admin@umpire-iq.com.');
      } finally {
        setClaiming(false);
        setClaimToken(null);
        setPage(session ? 'dashboard' : 'login');
      }
    };
    executeClaim();
  }, [claimToken, checked]); // eslint-disable-line

    // Inactivity timeout — sign out after INACTIVITY_LIMIT of no activity
  useIdleLogout({ enabled: !!session, timeoutMs: INACTIVITY_LIMIT });

  const navigate = (p) => {
    setPage(p);
    window.scrollTo(0, 0);
  };

  if (!checked || claiming) return null;

  if (page === 'privacy')        return <PrivacyPolicy   onBack={() => navigate('home')} />;
  if (page === 'terms')          return <TermsOfService   onBack={() => navigate('home')} />;
  if (page === 'signup')         return <Signup           onNavigate={navigate} />;
  if (page === 'login')          return <Login            onNavigate={navigate} />;
  if (page === 'forgot')         return <ForgotPassword   onNavigate={navigate} />;
  if (page === 'reset-password') return <ResetPassword    onNavigate={navigate} />;
  if (page === 'dashboard')      return <Dashboard        onNavigate={navigate} verificationResult={verificationResult} />;
  if (page === 'search')         return <Search           onNavigate={navigate} session={session} />;
  if (page === 'backoffice') return ( <Suspense fallback={null}> <BackOffice onNavigate={navigate} /> </Suspense> );  
  if (page === 'blog')           return <Blog             onNavigate={navigate} />;
  return <LandingPage onNavigate={navigate} />;
};

export default App;